// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import { createdAtField, getCreatedAtSort, idField, paymentModeField } from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import PaymentMethod from './PaymentMethod';

class User implements StoredEntity {
    static classInternalName = 'user';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        firstName: {
            displayName: 'First Name',
            recordName: 'first_name',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        lastName: {
            displayName: 'Last Name',
            recordName: 'last_name',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        phone: {
            displayName: 'Phone',
            recordName: 'phone',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        preferredPaymentMethodId: {
            displayName: 'Preferred Payment Method',
            recordName: 'preferred_payment_method_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => PaymentMethod,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: false,
            isOptional: true,
            displayOnMain: true,
        },
        defaultTipPercent: {
            displayName: 'Default Tip %',
            recordName: 'default_tip_percent',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        enabled: {
            displayName: 'Enabled',
            recordName: 'enabled',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        hasDevAccess: {
            displayName: 'Has Dev Access',
            recordName: 'has_dev_access',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        preferredLanguageCode: {
            displayName: 'Preferred Language',
            recordName: 'preferred_language_code',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        preferredCountryCode: {
            displayName: 'Preferred Locale Country',
            recordName: 'preferred_country_code',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        paymentMode: {
            ...paymentModeField,
            canEdit: true,
        },
        isAppclipUser: {
            displayName: 'App Clip User',
            recordName: 'is_appclip_user',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        deviceId: {
            displayName: 'Device ID',
            recordName: 'device_id',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: false,
            isOptional: true,
            displayOnMain: true,
        },
    };

    static fileFields: EntityFieldCollection = {};

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[User.fields.id.recordName];
    }

    get displayName() {
        const firstName = this.record[User.fields.firstName.recordName];
        const lastName = this.record[User.fields.lastName.recordName];
        
        if (firstName === 'Guest' && lastName) {
            return `Guest (${lastName.substring(0, 8)})`;
        }
        
        if (firstName && lastName) {
            return `${firstName} ${lastName}`;
        }
        
        return firstName || this.record[User.fields.phone.recordName];
    }

    static endpoints: Endpoints = constructEndpoints('users');

    constructor(record: any) {
        this.record = record;
    }
}

export default User;
